import React, { useState, useRef, useEffect } from 'react';
import { Autocomplete, AutocompleteOption, Box, FormHelperText, Typography } from '@mui/joy';
import type { SxProps } from '@mui/joy/styles/types';
import { MagnifyingGlass as Search } from '@phosphor-icons/react';
import type { ParsedAddress } from '../../types';

interface AddressAutocompleteInputProps {
  value: string;
  onChange: (value: string) => void;
  onAddressSelect: (parsed: ParsedAddress) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  sx?: SxProps;
}

type Prediction = google.maps.places.AutocompletePrediction;

export function parseAddressComponents(components: google.maps.GeocoderAddressComponent[]): Omit<ParsedAddress, 'lat' | 'lng' | 'place_id'> {
  const part = (type: string, short = false) => {
    const component = components.find(item => item.types.includes(type));
    return (short ? component?.short_name : component?.long_name) || '';
  };
  return {
    address_1: [part('street_number'), part('route')].filter(Boolean).join(' '),
    city: part('locality') || part('postal_town') || part('sublocality_level_1'),
    state: part('administrative_area_level_1', true),
    zip_code: part('postal_code'),
  };
}

export function formatAddress(parsed: ParsedAddress): string {
  return [parsed.address_1, parsed.city, [parsed.state, parsed.zip_code].filter(Boolean).join(' ')].filter(Boolean).join(', ');
}

export const AddressAutocompleteInput: React.FC<AddressAutocompleteInputProps> = ({
  value, onChange, onAddressSelect, placeholder = 'Start typing an address', required, disabled, sx,
}) => {
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const autocomplete = useRef<google.maps.places.AutocompleteService | null>(null);
  const places = useRef<google.maps.places.PlacesService | null>(null);
  const session = useRef<google.maps.places.AutocompleteSessionToken | undefined>(undefined);
  const debounce = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const request = useRef(0);
  const mounted = useRef(false);
  const focused = useRef(false);
  const currentValue = useRef(value);
  currentValue.current = value;

  useEffect(() => {
    mounted.current = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    const init = () => {
      if (!mounted.current) return;
      if (window.google?.maps?.places) {
        autocomplete.current = new window.google.maps.places.AutocompleteService();
        // Place details need an attribution container, not a hidden billable map.
        places.current = new window.google.maps.places.PlacesService(document.createElement('div'));
      } else if (++attempts < 100) {
        timer = setTimeout(init, 200);
      }
    };
    init();
    return () => {
      mounted.current = false;
      request.current++;
      clearTimeout(timer);
      clearTimeout(debounce.current);
    };
  }, []);

  const search = (query: string) => {
    onChange(query);
    const version = ++request.current;
    clearTimeout(debounce.current);
    setPredictions([]);
    setOpen(false);
    setLoading(false);
    setError('');
    if (query.trim().length < 3) return;
    debounce.current = setTimeout(() => {
      if (!autocomplete.current) {
        setError('Address suggestions are unavailable. You can enter the address manually.');
        return;
      }
      setLoading(true);
      session.current ??= new window.google.maps.places.AutocompleteSessionToken();
      autocomplete.current.getPlacePredictions(
        { input: query, types: ['address'], componentRestrictions: { country: 'us' }, sessionToken: session.current },
        (results, status) => {
          if (!mounted.current || version !== request.current || currentValue.current !== query || !focused.current) return;
          setLoading(false);
          if (status === 'OK' && results) {
            setPredictions(results);
            setOpen(true);
          } else if (status !== 'ZERO_RESULTS') {
            setError('Address suggestions are unavailable. You can enter the address manually.');
          }
        },
      );
    }, 400);
  };

  const select = (prediction: Prediction) => {
    if (!places.current) return;
    const version = ++request.current;
    const selectedFrom = currentValue.current;
    setOpen(false);
    setLoading(true);
    places.current.getDetails(
      { placeId: prediction.place_id, fields: ['address_components', 'geometry'], sessionToken: session.current },
      (result, status) => {
        if (!mounted.current || version !== request.current || currentValue.current !== selectedFrom) return;
        setLoading(false);
        session.current = undefined;
        if (status !== 'OK' || !result?.address_components) {
          setError('Could not load that address. Try again or enter it manually.');
          return;
        }
        const parsed = parseAddressComponents(result.address_components);
        if (!parsed.address_1) {
          setError('That result has no street address. Enter it manually.');
          return;
        }
        setError('');
        setPredictions([]);
        onChange(parsed.address_1);
        onAddressSelect({ ...parsed, lat: result.geometry?.location?.lat(), lng: result.geometry?.location?.lng(), place_id: prediction.place_id });
      },
    );
  };

  return (
    <>
      <Autocomplete<Prediction, false, false, true>
        freeSolo
        autoHighlight
        value={null}
        inputValue={value}
        options={predictions}
        filterOptions={options => options}
        getOptionLabel={option => typeof option === 'string' ? option : option.description}
        isOptionEqualToValue={(option, selected) => option.place_id === selected.place_id}
        onInputChange={(_, text, reason) => { if (reason === 'input' || reason === 'clear') search(text); }}
        onChange={(_, option) => { if (option && typeof option !== 'string') select(option); }}
        open={!disabled && open && predictions.length > 0}
        onOpen={() => setOpen(predictions.length > 0)}
        onClose={(_, reason) => {
          setOpen(false);
          if (reason !== 'selectOption') { request.current++; clearTimeout(debounce.current); setLoading(false); }
        }}
        onFocus={() => { focused.current = true; }}
        onBlur={() => { focused.current = false; clearTimeout(debounce.current); setLoading(false); }}
        loading={loading}
        disabled={disabled}
        required={required}
        placeholder={placeholder}
        startDecorator={<Search size={16} />}
        slotProps={{ input: { autoComplete: 'off' }, listbox: { sx: { zIndex: 1500, maxHeight: 250, bgcolor: 'background.surface' } } }}
        sx={sx}
        renderOption={(props, option) => (
          <AutocompleteOption {...props} key={option.place_id}>
            <Box>
              <Typography level="body-sm" fontWeight="md">{option.structured_formatting.main_text}</Typography>
              <Typography level="body-xs">{option.structured_formatting.secondary_text}</Typography>
            </Box>
          </AutocompleteOption>
        )}
      />
      {error && <FormHelperText role="status">{error}</FormHelperText>}
    </>
  );
};
